import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Search, Plus, Trash2, Loader2, AlertTriangle, Download, Play, X, Send, ExternalLink, Bot, Check, XCircle, Copy, Edit3, Clock, CheckCircle2, Zap, MessageSquare, ChevronDown, ChevronUp, RotateCcw, Save } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMOTION_COLORS = {
  lonely: '#6366F1',
  struggling: '#EF4444',
  motivation: '#10B981',
  companionship: '#EC4899',
  confidence: '#F5831F',
  general: '#6B7280',
};

const STATUS_COLORS = {
  new: { bg: '#F3F4F6', text: '#374151' },
  contacted: { bg: '#DBEAFE', text: '#1E40AF' },
  qualified: { bg: '#DCFCE7', text: '#166534' },
  converted: { bg: '#FEF3C7', text: '#92400E' },
  advocate: { bg: '#EDE9FE', text: '#7B4FD8' },
  archived: { bg: '#F3F4F6', text: '#9CA3AF' },
};

const STATUSES = ['new', 'contacted', 'qualified', 'converted', 'advocate', 'archived'];

const AdminLeads = () => {
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  // Sprint 207U — Score column sort toggle: 'desc' (High → Low) by default,
  // 'asc' (Low → High) on second click, null = unsorted (original order).
  const [scoreSort, setScoreSort] = useState('desc');
  // Lead Table data search — hits the DB (all recent & old leads), not just the loaded page.
  const [leadSearch, setLeadSearch] = useState('');
  const [dateSort, setDateSort] = useState('newest');
  const [sourceFilter, setSourceFilter] = useState('all'); // all | reddit | quora
  const [keywords, setKeywords] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState(null);
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategory, setNewCategory] = useState('confidence');
  const [subs, setSubs] = useState({ builtin: [], custom: [] });
  const [newSub, setNewSub] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadNotes, setLeadNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [responses, setResponses] = useState([]);
  const [responseStats, setResponseStats] = useState(null);
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [runNowResult, setRunNowResult] = useState(null);
  const [responseFilter, setResponseFilter] = useState('all');
  const [editingResponse, setEditingResponse] = useState(null);
  const [editText, setEditText] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDraft, setTemplateDraft] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [resettingTemplates, setResettingTemplates] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewingCategory, setPreviewingCategory] = useState(null);
  const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

  // Wrap `headers` in useMemo keyed on the tracked `token` so its identity
  // stays stable across renders. Previously this was a fresh object literal
  // per render, which meant listing it in any useCallback deps below would
  // rebuild the callback on every parent re-render → tight fetch loop.
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const loadData = useCallback(async () => {
    try {
      const [statsRes, leadsRes, kwRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/leads/summary`, { headers }),
        fetch(`${API_URL}/api/admin/leads?limit=100`, { headers }),
        fetch(`${API_URL}/api/admin/leads/keywords`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) { const d = await leadsRes.json(); setLeads(d.leads || []); }
      if (kwRes.ok) { const d = await kwRes.json(); setKeywords(d.keywords || []); }
    } catch (e) { console.error('Load error:', e); }
  }, [headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const searchLeads = useCallback(async (qv, sv, src) => {
    try {
      const params = new URLSearchParams({ limit: '300', sort: sv || 'newest' });
      if (qv && qv.trim()) params.set('q', qv.trim());
      if (src && src !== 'all') params.set('source', src);
      const r = await fetch(`${API_URL}/api/admin/leads?${params}`, { headers });
      if (r.ok) { const d = await r.json(); setLeads(d.leads || []); }
    } catch (e) { console.error(e); }
  }, [headers]);

  const loadResponses = useCallback(async () => {
    try {
      const params = responseFilter !== 'all' ? `?status=${responseFilter}` : '';
      const [respRes, schedRes, tmplRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/leads/responses${params}`, { headers }),
        fetch(`${API_URL}/api/admin/leads/scheduler/status`, { headers }),
        fetch(`${API_URL}/api/admin/leads/templates`, { headers }),
      ]);
      if (respRes.ok) {
        const d = await respRes.json();
        setResponses(d.responses || []);
        setResponseStats(d.stats || null);
      }
      if (schedRes.ok) setSchedulerInfo(await schedRes.json());
      if (tmplRes.ok) {
        const t = await tmplRes.json();
        setTemplates(t.templates || []);
      }
    } catch (e) { console.error('Load responses error:', e); }
  }, [responseFilter, headers]);

  useEffect(() => { if (tab === 'responses') loadResponses(); }, [tab, loadResponses]);

  const runSearch = async () => {
    if (!searchQuery) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/search`, {
        method: 'POST', headers, body: JSON.stringify({ query: searchQuery }),
      });
      if (res.ok) { await loadData(); setSearchQuery(''); }
    } catch (e) { console.error(e); }
    setSearching(false);
  };

  const runDiscovery = () => startDiscovery('discover');
  const runQuoraDiscovery = () => startDiscovery('discover-quora');
  const runAiRescore = () => startDiscovery('ai-rescore');

  const pollDiscovery = async () => {
    for (let i = 0; i < 150; i++) {
      await new Promise(r => setTimeout(r, 4000));
      try {
        const res = await fetch(`${API_URL}/api/admin/leads/discovery-status`, { headers });
        if (!res.ok) continue;
        const st = await res.json();
        if (st.status === 'running') { setDiscoveryProgress(st.progress || null); continue; }
        if (st.status === 'done') { setDiscoveryResult(st.result); await loadData(); }
        else if (st.status === 'error') setDiscoveryResult({ status: 'error', detail: st.error });
        return;
      } catch (e) { /* transient network error — keep polling */ }
    }
    setDiscoveryResult({ status: 'error', detail: 'Discovery is still running in the background — refresh this page in a few minutes to see the results.' });
  };

  const startDiscovery = async (endpoint) => {
    setDiscovering(true);
    setDiscoveryResult(null);
    setDiscoveryProgress(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${endpoint}`, { method: 'POST', headers });
      const result = await res.json();
      if (!res.ok) {
        setDiscoveryResult({ status: 'error', detail: result.detail });
      } else if (result.status === 'started' || result.status === 'already_running') {
        await pollDiscovery();
      } else {
        setDiscoveryResult(result);
        await loadData();
      }
    } catch (e) { setDiscoveryResult({ status: 'error', detail: e.message }); }
    setDiscovering(false);
    setDiscoveryProgress(null);
  };

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/export-csv`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'hibff_leads_export.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('CSV export failed:', e); }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/leads/${leadId}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status }),
      });
      await loadData();
    } catch (e) { console.error(e); }
  };

  const addKeyword = async () => {
    if (!newKeyword) return;
    try {
      await fetch(`${API_URL}/api/admin/leads/keywords`, {
        method: 'POST', headers, body: JSON.stringify({ keyword: newKeyword, category: (newCategory || 'general').trim().toLowerCase(), action: 'add' }),
      });
      setNewKeyword('');
      await loadData();
    } catch (e) { console.error(e); }
  };

  const loadSubs = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/leads/subreddits`, { headers });
      if (r.ok) setSubs(await r.json());
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { if (tab === 'keywords') loadSubs(); }, [tab, loadSubs]);

  const addSubreddit = async () => {
    if (!newSub.trim()) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/leads/subreddits`, {
        method: 'POST', headers, body: JSON.stringify({ name: newSub.trim(), action: 'add' }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); alert(d?.detail || 'Could not add subreddit'); return; }
      setNewSub('');
      await loadSubs();
    } catch (e) { console.error(e); }
  };

  const removeSubreddit = async (name) => {
    try {
      await fetch(`${API_URL}/api/admin/leads/subreddits`, {
        method: 'POST', headers, body: JSON.stringify({ name, action: 'remove' }),
      });
      await loadSubs();
    } catch (e) { console.error(e); }
  };

  const removeKeyword = async (kw) => {
    try {
      await fetch(`${API_URL}/api/admin/leads/keywords`, {
        method: 'POST', headers, body: JSON.stringify({ keyword: kw, action: 'remove' }),
      });
      await loadData();
    } catch (e) { console.error(e); }
  };

  const openLeadDetail = async (lead) => {
    setSelectedLead(lead);
    setNoteText('');
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${lead.lead_id}`, { headers });
      if (res.ok) {
        const detail = await res.json();
        setLeadNotes(detail.notes || []);
      }
    } catch (e) { console.error(e); setLeadNotes([]); }
  };

  const saveNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${selectedLead.lead_id}/notes`, {
        method: 'POST', headers, body: JSON.stringify({ text: noteText.trim() }),
      });
      if (res.ok) {
        const note = await res.json();
        setLeadNotes(prev => [note, ...prev]);
        setNoteText('');
      }
    } catch (e) { console.error(e); }
    setSavingNote(false);
  };

  const triggerRunNow = async () => {
    setRunningNow(true);
    setRunNowResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/scheduler/run-now`, { method: 'POST', headers });
      if (res.ok) {
        const result = await res.json();
        setRunNowResult(result);
        setTimeout(() => loadResponses(), 5000);
      }
    } catch (e) { console.error(e); }
    setRunningNow(false);
  };

  const updateResponseAction = async (responseId, action, editedText) => {
    try {
      const body = { action };
      if (action === 'edit' && editedText) body.edited_text = editedText;
      const res = await fetch(`${API_URL}/api/admin/leads/responses/${responseId}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      if (res.ok) {
        await loadResponses();
        setEditingResponse(null);
        setEditText('');
      }
    } catch (e) { console.error(e); }
  };

  const generateResponseForLead = async (leadId) => {
    setGeneratingFor(leadId);
    try {
      await fetch(`${API_URL}/api/admin/leads/${leadId}/generate-response`, { method: 'POST', headers });
      await loadResponses();
    } catch (e) { console.error(e); }
    setGeneratingFor(null);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const saveTemplate = async (category) => {
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/templates/${category}`, {
        method: 'PUT', headers, body: JSON.stringify({ brief: templateDraft }),
      });
      if (res.ok) {
        setTemplates(prev => prev.map(t => t.category === category ? { ...t, brief: templateDraft } : t));
        setEditingTemplate(null);
        setTemplateDraft('');
      }
    } catch (e) { console.error(e); }
    setSavingTemplate(false);
  };

  const resetAllTemplates = async () => {
    if (!window.confirm('Reset all personality briefs to defaults? Your customisations will be lost.')) return;
    setResettingTemplates(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/templates/reset`, { method: 'POST', headers });
      if (res.ok) await loadResponses();
    } catch (e) { console.error(e); }
    setResettingTemplates(false);
  };

  const previewTemplate = async (category) => {
    setPreviewingCategory(category);
    setPreviewData(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/templates/${category}/preview`, { method: 'POST', headers });
      if (res.ok) setPreviewData(await res.json());
    } catch (e) { console.error(e); }
    setPreviewingCategory(null);
  };

  // Compute analytics data
  const emotionData = {};
  const subredditData = {};
  const scoreDistribution = { 'High (70+)': 0, 'Medium (30-69)': 0, 'Low (<30)': 0 };
  leads.forEach(l => {
    emotionData[l.emotion_category || 'general'] = (emotionData[l.emotion_category || 'general'] || 0) + 1;
    const sub = l.subreddit || 'unknown';
    subredditData[sub] = (subredditData[sub] || 0) + 1;
    if (l.score >= 70) scoreDistribution['High (70+)']++;
    else if (l.score >= 30) scoreDistribution['Medium (30-69)']++;
    else scoreDistribution['Low (<30)']++;
  });

  const emotionChartData = Object.entries(emotionData).map(([name, value]) => ({ name, value }));
  const subredditChartData = Object.entries(subredditData).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, count]) => ({ name: `r/${name}`, count }));
  const scoreChartData = Object.entries(scoreDistribution).map(([name, count]) => ({ name, count }));

  const qualifiedLeads = leads.filter(l => l.score >= 55);

  return (
    <AdminLayout pageTitle="Lead Generation" data-testid="admin-leads-page">
      <CheatSheet pageId="leads" />
      {/* API Connection Status */}
      <div className="bg-white p-4 rounded-lg mb-6 flex items-center justify-between" style={{ border: '1px solid #E0E0E0' }} data-testid="api-connection-status">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${stats?.connection_status === 'connected' ? 'bg-green-500' : stats?.connection_status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`} data-testid="reddit-status-indicator" />
            <span className="text-sm font-medium" style={{ color: '#333333' }}>Reddit API</span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: stats?.connection_status === 'connected' ? '#DCFCE7' : '#FEF3C7', color: stats?.connection_status === 'connected' ? '#166534' : '#92400E' }} data-testid="reddit-status-badge">{stats?.connection_status || 'unknown'}</span>
          </div>
          <div className="flex items-center gap-2 opacity-40">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-sm" style={{ color: '#374151' }}>LinkedIn (future)</span>
          </div>
          <div className="flex items-center gap-2 opacity-40">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-sm" style={{ color: '#374151' }}>Google (future)</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stats?.last_error && (
            <div className="flex items-center gap-2 text-xs text-amber-700"><AlertTriangle size={14} />{stats.last_error}</div>
          )}
        </div>
      </div>

      {/* Search + Actions Row */}
      <div className="bg-white p-4 rounded-lg mb-6 flex flex-wrap gap-3" style={{ border: '1px solid #E0E0E0' }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Reddit (e.g. 'teenager no friends', 'social anxiety school')..."
          className="flex-1 min-w-[200px] px-4 py-2.5 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}
          onKeyDown={e => e.key === 'Enter' && runSearch()} data-testid="lead-search-input" />
        <button onClick={runSearch} disabled={searching || !searchQuery}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-2"
          style={{ backgroundColor: '#F5831F' }} data-testid="lead-search-btn">
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
        </button>
        <button onClick={runDiscovery} disabled={discovering}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-2"
          style={{ backgroundColor: '#7B4FD8' }} data-testid="run-discovery-btn">
          {discovering ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Run Discovery
        </button>
        <button onClick={runQuoraDiscovery} disabled={discovering}
          className="px-4 py-2.5 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50"
          style={{ backgroundColor: '#B92B27' }} data-testid="run-quora-discovery-btn"
          title="Search Quora for long-form parent questions using your discovery keywords">
          {discovering ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Quora
        </button>
        <button onClick={runAiRescore} disabled={discovering}
          className="px-4 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          style={{ backgroundColor: '#0F766E' }} data-testid="ai-rescore-btn"
          title="AI reads every unreviewed lead, removes junk and re-scores the rest">
          {discovering ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />} AI Clean-Up
        </button>
        <button onClick={exportCSV}
          className="px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 border"
          style={{ borderColor: '#E0E0E0', color: '#333' }} data-testid="export-csv-btn">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Discovery result banner */}
      {discovering && (
        <div className="p-3 rounded-lg mb-4 text-sm bg-blue-50 text-blue-800 border border-blue-200 flex items-center gap-2" data-testid="discovery-progress-banner">
          <Loader2 size={14} className="animate-spin" />
          Working in the background{discoveryProgress?.total ? ` — ${discoveryProgress.done}/${discoveryProgress.total} processed.` : '…'} Results update as they're found.
        </div>
      )}
      {discoveryResult && (
        <div className={`p-4 rounded-lg mb-6 text-sm ${(discoveryResult.status === 'success' || discoveryResult.status === 'completed' || discoveryResult.status === 'rescored') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-testid="discovery-result-banner">
          {discoveryResult.status === 'success' ? (
            <div>
              <p className="font-semibold mb-1">Discovery complete: {discoveryResult.new_leads_saved} new leads saved from {discoveryResult.keywords_searched} keywords</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {discoveryResult.results?.map((r, i) => (
                  <span key={i} className="text-xs bg-white px-2 py-1 rounded border border-green-200">
                    &quot;{r.keyword}&quot; — {r.saved || 0} new {r.error ? `(error: ${r.error})` : ''}
                  </span>
                ))}
              </div>
            </div>
          ) : discoveryResult.status === 'completed' ? (
            <div>
              <p className="font-semibold mb-1">Quora discovery complete: {discoveryResult.new_leads} new leads from {discoveryResult.total_found} results across {discoveryResult.keywords_used} keywords</p>
              {discoveryResult.note && <p className="text-xs mt-1" style={{ color: '#92400E' }}>{discoveryResult.note}</p>}
            </div>
          ) : discoveryResult.status === 'rescored' ? (
            <p className="font-semibold">AI clean-up complete: {discoveryResult.scanned} leads reviewed — {discoveryResult.removed} junk removed, {discoveryResult.kept} kept &amp; re-scored</p>
          ) : (
            <p>{discoveryResult.detail || 'Discovery failed.'}</p>
          )}
        </div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Leads', value: stats?.total_leads || leads.length },
          { label: 'This Week', value: stats?.total_this_week || 0 },
          { label: 'Qualified (55+)', value: stats?.qualified || qualifiedLeads.length },
          { label: 'Conversion Rate', value: `${stats?.conversion_rate || 0}%` },
        ].map((m, i) => (
          <div key={i} className="bg-white p-4 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid={`stat-card-${i}`}>
            <p className="text-sm font-semibold" style={{ color: '#374151' }}>{m.label}</p>
            <p className="text-3xl font-bold mt-1" style={{ color: '#0A0A0A' }}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4" data-testid="leads-tabs">
        {[
          { id: 'leads', label: 'Lead Table' },
          { id: 'responses', label: 'Auto-Responses' },
          { id: 'keywords', label: 'Discovery Keywords' },
          { id: 'analytics', label: 'Analytics' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${tab === t.id ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            style={tab === t.id ? { color: '#1A1209' } : {}}
            data-testid={`tab-${t.id}`}>
            {t.id === 'responses' && <Bot size={14} />}
            {t.label}
            {t.id === 'responses' && responseStats?.drafts > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full text-white" style={{ backgroundColor: '#F5831F' }}>
                {responseStats.drafts}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lead Table Tab */}
      {tab === 'leads' && (
        <div className="bg-white rounded-lg overflow-x-auto" style={{ border: '1px solid #E0E0E0' }} data-testid="leads-table">
          <div className="flex flex-wrap items-center gap-2 p-3" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid="lead-data-search-bar">
            <input
              value={leadSearch}
              onChange={e => setLeadSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchLeads(leadSearch, dateSort, sourceFilter)}
              placeholder="Search all lead data — title, subreddit, category, post text, status…"
              className="flex-1 min-w-[260px] px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}
              data-testid="lead-data-search-input"
            />
            <button onClick={() => searchLeads(leadSearch, dateSort, sourceFilter)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#F5831F' }}
              data-testid="lead-data-search-btn">Search</button>
            {leadSearch && (
              <button onClick={() => { setLeadSearch(''); searchLeads('', dateSort, sourceFilter); }}
                className="px-3 py-2 rounded-lg text-sm" style={{ color: '#6B7280', border: '1px solid #E0E0E0' }}
                data-testid="lead-data-search-clear">Clear</button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F3F4F6' }}>
              {['Source', 'Subreddit', 'Title', 'Category', 'Score', 'Status', 'Date', 'Actions'].map(h => {
                const isScore = h === 'Score';
                const isDate = h === 'Date';
                const isSource = h === 'Source';
                return (
                  <th
                    key={h}
                    className={`text-left p-3 text-sm font-bold whitespace-nowrap ${(isScore || isDate || isSource) ? 'cursor-pointer hover:bg-gray-200 select-none' : ''}`}
                    style={{ color: '#0A0A0A' }}
                    onClick={
                      isScore ? () => setScoreSort(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc')
                      : isDate ? () => { const next = dateSort === 'newest' ? 'oldest' : 'newest'; setDateSort(next); setScoreSort(null); searchLeads(leadSearch, next, sourceFilter); }
                      : isSource ? () => { const next = sourceFilter === 'all' ? 'reddit' : sourceFilter === 'reddit' ? 'quora' : 'all'; setSourceFilter(next); searchLeads(leadSearch, dateSort, next); }
                      : undefined
                    }
                    data-testid={isScore ? 'leads-score-header' : isDate ? 'leads-date-header' : isSource ? 'leads-source-header' : undefined}
                    title={isScore ? 'Click to sort by score' : isDate ? 'Click to toggle recent/old' : isSource ? 'Click to filter: All → Reddit → Quora' : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {h}
                      {isScore && (
                        scoreSort === 'desc' ? <ChevronDown size={14} className="text-purple-700" data-testid="score-sort-icon-desc" />
                        : scoreSort === 'asc' ? <ChevronUp size={14} className="text-purple-700" data-testid="score-sort-icon-asc" />
                        : <ChevronDown size={14} className="opacity-30" />
                      )}
                      {isScore && scoreSort && (
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-purple-700" data-testid="score-sort-label">
                          {scoreSort === 'desc' ? 'High→Low' : 'Low→High'}
                        </span>
                      )}
                      {isDate && (
                        dateSort === 'newest' ? <ChevronDown size={14} className="text-purple-700" /> : <ChevronUp size={14} className="text-purple-700" />
                      )}
                      {isDate && (
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-purple-700" data-testid="date-sort-label">
                          {dateSort === 'newest' ? 'Recent' : 'Oldest'}
                        </span>
                      )}
                      {isSource && (
                        sourceFilter === 'all'
                          ? <ChevronDown size={14} className="opacity-30" />
                          : <span className="ml-1 text-[10px] font-bold uppercase tracking-wide"
                              style={{ color: sourceFilter === 'quora' ? '#B92B27' : '#7B4FD8' }}
                              data-testid="source-filter-label">{sourceFilter}</span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr></thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-sm" style={{ color: '#374151' }}>No leads discovered yet. Use the search bar or Run Discovery to find leads.</td></tr>
              ) : (
                (() => {
                  const bySource = sourceFilter === 'all' ? leads
                    : leads.filter(l => (sourceFilter === 'quora' ? l.source === 'Quora' : l.source !== 'Quora'));
                  const sorted = scoreSort
                    ? [...bySource].sort((a, b) => {
                        const av = Number(a.score || 0); const bv = Number(b.score || 0);
                        return scoreSort === 'desc' ? bv - av : av - bv;
                      })
                    : bySource;
                  if (sorted.length === 0) {
                    return <tr><td colSpan={8} className="p-8 text-center text-sm" style={{ color: '#374151' }}>No {sourceFilter !== 'all' ? sourceFilter.charAt(0).toUpperCase() + sourceFilter.slice(1) : ''} leads in this view.</td></tr>;
                  }
                  return sorted.map((lead, i) => (
                <tr key={i} className="border-t hover:bg-gray-50 transition-colors cursor-pointer" style={{ borderColor: '#E0E0E0' }}
                  onClick={() => openLeadDetail(lead)} data-testid={`lead-row-${i}`}>
                  <td className="p-3 text-sm" style={{ color: '#374151' }}>{lead.source || 'Reddit'}</td>
                  <td className="p-3 text-sm font-semibold" style={{ color: lead.subreddit ? '#7B4FD8' : '#B92B27' }}>{lead.subreddit ? `r/${lead.subreddit}` : (lead.source === 'Quora' ? 'Quora' : '—')}</td>
                  <td className="p-3 text-sm max-w-[300px] truncate" style={{ color: '#1F2937' }} title={lead.title}>{lead.title || '—'}</td>
                  <td className="p-3">
                    <span className="px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap"
                      style={{ backgroundColor: `${EMOTION_COLORS[lead.emotion_category] || '#374151'}15`, color: EMOTION_COLORS[lead.emotion_category] || '#374151' }}>
                      {lead.emotion_category || 'general'}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`text-base font-bold ${lead.score >= 70 ? 'text-green-700' : lead.score >= 30 ? 'text-yellow-700' : 'text-gray-700'}`} data-testid={`lead-score-${i}`}>{lead.score || 0}</span>
                  </td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <select value={lead.status || 'new'} onChange={e => updateLeadStatus(lead.lead_id, e.target.value)}
                      className="text-sm px-2 py-1 rounded-full font-semibold border-0"
                      style={{ backgroundColor: (STATUS_COLORS[lead.status] || STATUS_COLORS.new).bg, color: (STATUS_COLORS[lead.status] || STATUS_COLORS.new).text }}
                      data-testid={`lead-status-${i}`}>
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-sm whitespace-nowrap" style={{ color: '#374151' }}>{lead.discovered_at?.slice(0, 10)}</td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <a href={lead.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold" style={{ color: '#F5831F' }} data-testid={`lead-link-${i}`}>View Post</a>
                  </td>
                </tr>
                  ));
                })()
              )}
            </tbody>
          </table>
          {leads.length > 0 && (
            <div className="p-3 border-t text-sm text-right" style={{ borderColor: '#E0E0E0', color: '#374151' }}>
              Showing {leads.length} leads ({qualifiedLeads.length} qualified)
            </div>
          )}
        </div>
      )}

      {/* Auto-Responses Tab */}
      {tab === 'responses' && (
        <div className="space-y-4" data-testid="responses-panel">
          {/* Scheduler Status Bar */}
          <div className="bg-white p-4 rounded-lg flex flex-wrap items-center justify-between gap-3" style={{ border: '1px solid #E0E0E0' }} data-testid="scheduler-status-bar">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Clock size={16} style={{ color: '#7B4FD8' }} />
                <span className="text-sm font-medium" style={{ color: '#333' }}>Scheduler</span>
              </div>
              <div className="flex items-center gap-1.5">
                {schedulerInfo?.schedule?.active_days?.map(day => (
                  <span key={day} className="px-2 py-0.5 text-[10px] font-medium rounded-full"
                    style={{
                      backgroundColor: schedulerInfo?.schedule?.current_day === day ? '#F5831F' : '#F3F4F6',
                      color: schedulerInfo?.schedule?.current_day === day ? '#FFF' : '#666',
                    }}>
                    {day.slice(0, 3)}
                  </span>
                ))}
                <span className="text-xs ml-1" style={{ color: '#374151' }}>every 2h</span>
              </div>
              {schedulerInfo?.schedule?.is_active_today && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Active today
                </span>
              )}
              {schedulerInfo?.last_runs?.[0] && (
                <span className="text-xs" style={{ color: '#374151' }}>
                  Last run: {schedulerInfo.last_runs[0].processed_at?.slice(0, 16).replace('T', ' ')}
                  {schedulerInfo.last_runs[0].manual && ' (manual)'}
                </span>
              )}
            </div>
            <button onClick={triggerRunNow} disabled={runningNow}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#7B4FD8' }} data-testid="run-now-btn">
              {runningNow ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Run Now
            </button>
          </div>

          {runNowResult && (
            <div className="p-3 rounded-lg text-sm bg-purple-50 text-purple-800 border border-purple-200" data-testid="run-now-result">
              {runNowResult.status === 'accepted'
                ? 'Discovery + auto-response cycle started in background. Responses will appear shortly.'
                : `Found ${runNowResult.posts_found || 0} posts, saved ${runNowResult.new_leads_saved || 0} leads, generated ${runNowResult.responses_generated || 0} responses.`
              }
            </div>
          )}

          {/* Personality Templates — Collapsible */}
          <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="templates-section">
            <button onClick={() => setShowTemplates(prev => !prev)}
              className="w-full px-4 py-3 flex items-center justify-between text-left"
              style={{ backgroundColor: '#FAFAFA' }}
              data-testid="templates-toggle">
              <div className="flex items-center gap-2">
                <Bot size={16} style={{ color: '#7B4FD8' }} />
                <span className="text-sm font-medium" style={{ color: '#333' }}>Personality Briefs</span>
                <span className="text-xs" style={{ color: '#374151' }}>({templates.length} emotion categories)</span>
              </div>
              {showTemplates ? <ChevronUp size={16} style={{ color: '#374151' }} /> : <ChevronDown size={16} style={{ color: '#374151' }} />}
            </button>

            {showTemplates && (
              <div className="p-4 space-y-3" style={{ borderTop: '1px solid #F0F0F0' }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs" style={{ color: '#374151' }}>
                    Customize how the AI responds to each emotion category. The tier escalation (empathy → soft mention → direct link) is automatic.
                  </p>
                  <button onClick={resetAllTemplates} disabled={resettingTemplates}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border font-medium disabled:opacity-50"
                    style={{ borderColor: '#E0E0E0', color: '#374151' }}
                    data-testid="reset-templates-btn">
                    {resettingTemplates ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                    Reset Defaults
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {templates.map(tmpl => (
                    <div key={tmpl.category} className="rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid={`template-card-${tmpl.category}`}>
                      <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tmpl.color }} />
                          <span className="text-sm font-medium" style={{ color: '#333' }}>{tmpl.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => previewTemplate(tmpl.category)}
                            disabled={previewingCategory === tmpl.category}
                            className="text-[11px] flex items-center gap-1 disabled:opacity-50"
                            style={{ color: '#7B4FD8' }}
                            data-testid={`preview-template-${tmpl.category}`}>
                            {previewingCategory === tmpl.category
                              ? <><Loader2 size={11} className="animate-spin" /> Generating...</>
                              : <><Play size={11} /> Preview</>
                            }
                          </button>
                          <button
                            onClick={() => { setEditingTemplate(tmpl.category); setTemplateDraft(tmpl.brief); }}
                            className="text-[11px] flex items-center gap-1"
                            style={{ color: '#F5831F' }}
                            data-testid={`edit-template-${tmpl.category}`}>
                            <Edit3 size={11} /> Edit
                          </button>
                        </div>
                      </div>
                      <div className="px-3 py-2.5">
                        {editingTemplate === tmpl.category ? (
                          <div>
                            <textarea
                              value={templateDraft}
                              onChange={e => setTemplateDraft(e.target.value)}
                              className="w-full px-2.5 py-2 text-xs border rounded-lg resize-none leading-relaxed"
                              style={{ borderColor: '#E0E0E0', minHeight: '100px' }}
                              data-testid={`template-textarea-${tmpl.category}`}
                            />
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => saveTemplate(tmpl.category)}
                                disabled={savingTemplate}
                                className="px-2.5 py-1 text-[11px] rounded-md text-white font-medium flex items-center gap-1 disabled:opacity-50"
                                style={{ backgroundColor: '#10B981' }}
                                data-testid={`save-template-${tmpl.category}`}>
                                {savingTemplate ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                              </button>
                              <button
                                onClick={() => { setEditingTemplate(null); setTemplateDraft(''); }}
                                className="px-2.5 py-1 text-[11px] rounded-md border font-medium"
                                style={{ borderColor: '#E0E0E0', color: '#374151' }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs leading-relaxed" style={{ color: '#374151' }}>{tmpl.brief}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Preview Result Panel */}
                {previewData && (
                  <div className="rounded-lg overflow-hidden mt-3" style={{ border: '1px solid #E0E0E0' }} data-testid="preview-result-panel">
                    <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#F5F3FF', borderBottom: '1px solid #EDE9FE' }}>
                      <div className="flex items-center gap-2">
                        <Play size={13} style={{ color: '#7B4FD8' }} />
                        <span className="text-xs font-medium" style={{ color: '#7B4FD8' }}>Preview — {previewData.category}</span>
                      </div>
                      <button onClick={() => setPreviewData(null)} className="p-1 rounded hover:bg-purple-100" data-testid="close-preview-btn">
                        <X size={13} style={{ color: '#7B4FD8' }} />
                      </button>
                    </div>
                    <div className="px-4 py-3 space-y-3">
                      <div className="rounded-lg p-3" style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA' }}>
                        <p className="text-[10px] font-medium mb-1" style={{ color: '#F5831F' }}>SAMPLE POST — r/{previewData.sample_post?.subreddit}</p>
                        <p className="text-xs font-medium" style={{ color: '#1A1209' }}>{previewData.sample_post?.title}</p>
                        <p className="text-xs mt-1" style={{ color: '#374151' }}>{previewData.sample_post?.text}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-medium mb-1.5" style={{ color: '#7B4FD8' }}>AI RESPONSE (Tier 1 — Empathy Only)</p>
                        <div className="relative group">
                          <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#333' }} data-testid="preview-response-text">{previewData.preview_response}</p>
                          <button onClick={() => copyToClipboard(previewData.preview_response, 'preview')}
                            className="absolute top-0 right-0 p-1.5 rounded-lg bg-white border opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ borderColor: '#E0E0E0' }} data-testid="copy-preview-btn">
                            {copiedId === 'preview' ? <Check size={13} className="text-green-500" /> : <Copy size={13} style={{ color: '#374151' }} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Response Stats */}
          {responseStats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: 'Total', value: responseStats.total, color: '#1A1209' },
                { label: 'Drafts', value: responseStats.drafts, color: '#F59E0B' },
                { label: 'Approved', value: responseStats.approved, color: '#7B4FD8' },
                { label: 'Posted', value: responseStats.posted, color: '#10B981' },
                { label: 'Rejected', value: responseStats.rejected, color: '#EF4444' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 rounded-lg text-center" style={{ border: '1px solid #E0E0E0' }} data-testid={`response-stat-${s.label.toLowerCase()}`}>
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#374151' }}>{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filter pills */}
          <div className="flex gap-2" data-testid="response-filters">
            {['all', 'draft', 'approved', 'posted', 'rejected'].map(f => (
              <button key={f} onClick={() => setResponseFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium capitalize transition-all ${responseFilter === f ? 'text-white' : 'bg-gray-100'}`}
                style={responseFilter === f ? { backgroundColor: '#F5831F' } : { color: '#374151' }}
                data-testid={`filter-${f}`}>
                {f}
              </button>
            ))}
          </div>

          {/* Response Cards */}
          <div className="space-y-3">
            {responses.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center" style={{ border: '1px solid #E0E0E0' }} data-testid="no-responses-msg">
                <Bot size={40} className="mx-auto mb-3" style={{ color: '#E0E0E0' }} />
                <p className="text-sm font-medium" style={{ color: '#333' }}>No auto-responses yet</p>
                <p className="text-xs mt-1" style={{ color: '#374151' }}>Run discovery to find leads, then AI will generate draft responses automatically.</p>
              </div>
            ) : responses.map((r) => {
              const tierColors = {
                empathy_only: { bg: '#DBEAFE', text: '#1E40AF', label: 'Empathy Only' },
                soft_mention: { bg: '#EDE9FE', text: '#7B4FD8', label: 'Soft Mention' },
                direct_link: { bg: '#FEF3C7', text: '#92400E', label: 'Direct Link' },
              };
              const tier = tierColors[r.response_tier] || tierColors.empathy_only;
              const statusIcons = {
                draft: <Clock size={12} />,
                approved: <CheckCircle2 size={12} />,
                posted: <Check size={12} />,
                rejected: <XCircle size={12} />,
              };
              const statusStyles = {
                draft: { bg: '#FEF3C7', text: '#92400E' },
                approved: { bg: '#EDE9FE', text: '#7B4FD8' },
                posted: { bg: '#DCFCE7', text: '#166534' },
                rejected: { bg: '#FEE2E2', text: '#991B1B' },
              };
              const sStyle = statusStyles[r.status] || statusStyles.draft;

              return (
                <div key={r.response_id} className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid={`response-card-${r.response_id}`}>
                  {/* Card Header */}
                  <div className="px-4 py-3 flex items-start justify-between" style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #F0F0F0' }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium" style={{ color: '#7B4FD8' }}>r/{r.subreddit}</span>
                        <span className="text-xs" style={{ color: '#CCC' }}>&middot;</span>
                        <span className="text-xs" style={{ color: '#374151' }}>u/{r.author}</span>
                        <span className="text-xs" style={{ color: '#CCC' }}>&middot;</span>
                        <span className="text-xs" style={{ color: '#374151' }}>#{r.interaction_number}</span>
                      </div>
                      <p className="text-sm font-medium truncate" style={{ color: '#1A1209' }} title={r.post_title}>{r.post_title}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full" style={{ backgroundColor: tier.bg, color: tier.text }}>{tier.label}</span>
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full flex items-center gap-1" style={{ backgroundColor: sStyle.bg, color: sStyle.text }}>
                        {statusIcons[r.status]} {r.status}
                      </span>
                    </div>
                  </div>

                  {/* Response Text */}
                  <div className="px-4 py-3">
                    {editingResponse === r.response_id ? (
                      <div>
                        <textarea value={editText} onChange={e => setEditText(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
                          style={{ borderColor: '#E0E0E0', minHeight: '120px' }}
                          data-testid={`edit-textarea-${r.response_id}`} />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => updateResponseAction(r.response_id, 'edit', editText)}
                            className="px-3 py-1.5 text-xs rounded-lg text-white font-medium" style={{ backgroundColor: '#10B981' }}
                            data-testid={`save-edit-${r.response_id}`}>Save</button>
                          <button onClick={() => { setEditingResponse(null); setEditText(''); }}
                            className="px-3 py-1.5 text-xs rounded-lg border font-medium" style={{ borderColor: '#E0E0E0', color: '#374151' }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative group">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#444' }}>{r.response_text}</p>
                        <button onClick={() => copyToClipboard(r.response_text, r.response_id)}
                          className="absolute top-0 right-0 p-1.5 rounded-lg bg-white border opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ borderColor: '#E0E0E0' }}
                          title="Copy to clipboard"
                          data-testid={`copy-btn-${r.response_id}`}>
                          {copiedId === r.response_id ? <Check size={13} className="text-green-500" /> : <Copy size={13} style={{ color: '#374151' }} />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Card Footer — Actions */}
                  <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#FAFAFA', borderTop: '1px solid #F0F0F0' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px]" style={{ color: '#374151' }}>Score: {r.score}</span>
                      <span className="text-[10px]" style={{ color: '#374151' }}>{r.generated_at?.slice(0, 10)}</span>
                      {r.post_url && (
                        <a href={r.post_url} target="_blank" rel="noopener noreferrer" className="text-[10px] flex items-center gap-1" style={{ color: '#F5831F' }}>
                          <ExternalLink size={10} /> View post
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {r.status === 'draft' && (
                        <>
                          <button onClick={() => updateResponseAction(r.response_id, 'approve')}
                            className="px-2.5 py-1 text-[11px] rounded-md font-medium text-white flex items-center gap-1"
                            style={{ backgroundColor: '#7B4FD8' }}
                            data-testid={`approve-btn-${r.response_id}`}>
                            <CheckCircle2 size={11} /> Approve
                          </button>
                          <button onClick={() => updateResponseAction(r.response_id, 'reject')}
                            className="px-2.5 py-1 text-[11px] rounded-md font-medium flex items-center gap-1 border"
                            style={{ borderColor: '#E0E0E0', color: '#EF4444' }}
                            data-testid={`reject-btn-${r.response_id}`}>
                            <XCircle size={11} /> Reject
                          </button>
                        </>
                      )}
                      {r.status === 'approved' && (
                        <button onClick={() => updateResponseAction(r.response_id, 'posted')}
                          className="px-2.5 py-1 text-[11px] rounded-md font-medium text-white flex items-center gap-1"
                          style={{ backgroundColor: '#10B981' }}
                          data-testid={`posted-btn-${r.response_id}`}>
                          <Check size={11} /> Mark Posted
                        </button>
                      )}
                      <button onClick={() => { setEditingResponse(r.response_id); setEditText(r.response_text); }}
                        className="px-2.5 py-1 text-[11px] rounded-md font-medium flex items-center gap-1 border"
                        style={{ borderColor: '#E0E0E0', color: '#374151' }}
                        data-testid={`edit-btn-${r.response_id}`}>
                        <Edit3 size={11} /> Edit
                      </button>
                      <button onClick={() => copyToClipboard(r.response_text, r.response_id)}
                        className="px-2.5 py-1 text-[11px] rounded-md font-medium flex items-center gap-1 border"
                        style={{ borderColor: '#E0E0E0', color: '#374151' }}
                        data-testid={`copy-action-${r.response_id}`}>
                        {copiedId === r.response_id ? <><Check size={11} className="text-green-500" /> Copied</> : <><Copy size={11} /> Copy</>}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Keywords Tab */}
      {tab === 'keywords' && (
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="keywords-panel">
          {/* Discovery Subreddits manager */}
          <div className="mb-6 pb-5" style={{ borderBottom: '1px solid #F0F0F0' }} data-testid="subreddits-panel">
            <p className="text-sm font-bold mb-1" style={{ color: '#333' }}>Discovery Subreddits</p>
            <p className="text-xs mb-3" style={{ color: '#999' }}>Your added subreddits are searched first on every Run Discovery. Built-in targets shown in grey.</p>
            <div className="flex gap-2 mb-3">
              <input value={newSub} onChange={e => setNewSub(e.target.value)} placeholder="Add subreddit (e.g. ParentsOfTeens)"
                className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0', maxWidth: 360 }}
                onKeyDown={e => e.key === 'Enter' && addSubreddit()} data-testid="new-subreddit-input" />
              <button onClick={addSubreddit}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
                style={{ backgroundColor: '#7B4FD8' }} data-testid="add-subreddit-btn">
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(subs.custom || []).map(s => (
                <span key={s.name} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: '#7B4FD815', color: '#7B4FD8', border: '1px solid #7B4FD840' }}
                  data-testid={`custom-sub-${s.name}`}>
                  r/{s.name}
                  <button onClick={() => removeSubreddit(s.name)} aria-label={`Remove r/${s.name}`}
                    data-testid={`remove-sub-${s.name}`} className="hover:opacity-70">×</button>
                </span>
              ))}
              {(subs.builtin || []).map(s => (
                <span key={s} className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>r/{s}</span>
              ))}
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} placeholder="New keyword..."
              className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}
              onKeyDown={e => e.key === 'Enter' && addKeyword()} data-testid="new-keyword-input" />
            <input list="kw-categories" value={newCategory} onChange={e => setNewCategory(e.target.value)}
              placeholder="Category (e.g. confidence)"
              className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0', width: 200 }}
              data-testid="keyword-category-select" />
            <datalist id="kw-categories">
              {[...new Set([...Object.keys(EMOTION_COLORS), ...keywords.map(k => k.category).filter(Boolean)])]
                .map(c => <option key={c} value={c} />)}
            </datalist>
            <button onClick={addKeyword}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5"
              style={{ backgroundColor: '#F5831F' }} data-testid="add-keyword-btn">
              <Plus size={14} /> Add
            </button>
          </div>
          <div className="space-y-2">
            {keywords.length === 0 ? (
              <p className="text-sm py-4 text-center" style={{ color: '#374151' }} data-testid="no-keywords-msg">
                No discovery keywords configured. Add keywords to start monitoring Reddit for leads.
              </p>
            ) : keywords.map((kw, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F9F9F9' }} data-testid={`keyword-row-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                    style={{ backgroundColor: `${EMOTION_COLORS[kw.category] || '#666'}15`, color: EMOTION_COLORS[kw.category] || '#666' }}>
                    {kw.category}
                  </span>
                  <span className="text-sm font-medium" style={{ color: '#333333' }}>{kw.keyword}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: '#374151' }}>{kw.leads_generated || 0} leads</span>
                  <button onClick={() => removeKeyword(kw.keyword)} className="text-red-400 hover:text-red-600" data-testid={`remove-keyword-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {tab === 'analytics' && (
        <div className="space-y-4" data-testid="analytics-panel">
          {/* Source → Subscriber conversion */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="source-conversion-cards">
            {[
              ['Reddit', leads.filter(l => l.source !== 'Quora'), '#7B4FD8'],
              ['Quora', leads.filter(l => l.source === 'Quora'), '#B92B27'],
            ].map(([label, srcLeads, color]) => {
              const conv = srcLeads.filter(l => ['converted', 'advocate'].includes(l.status)).length;
              const rate = srcLeads.length ? ((conv / srcLeads.length) * 100).toFixed(1) : '0.0';
              return (
                <div key={label} className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid={`conversion-card-${String(label).toLowerCase()}`}>
                  <h3 className="text-sm font-bold mb-1" style={{ color }}>{label} → Subscribers</h3>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-black" style={{ color: '#1A1209' }}>{conv}</span>
                    <span className="text-sm" style={{ color: '#6B7280' }}>of {srcLeads.length} leads · {rate}% conversion</span>
                  </div>
                  <p className="text-[11px] mt-2" style={{ color: '#9CA3AF' }}>Auto-tracked: signups through this source&apos;s outreach links flip the lead to &quot;converted&quot;</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Emotion Distribution */}
            <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Emotion Distribution</h3>
              {emotionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart><Pie data={emotionChartData} dataKey="value" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {emotionChartData.map((d, i) => <Cell key={i} fill={EMOTION_COLORS[d.name] || '#666'} />)}
                  </Pie><Tooltip /></PieChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-center py-8" style={{ color: '#374151' }}>No data yet</p>}
            </div>

            {/* Score Distribution */}
            <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Score Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={scoreChartData}>
                  <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {scoreChartData.map((d, i) => <Cell key={i} fill={['#10B981', '#F59E0B', '#EF4444'][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Lead Funnel */}
            <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Lead Funnel</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[
                  { stage: 'Discovered', count: leads.length },
                  { stage: 'Contacted', count: leads.filter(l => ['contacted', 'qualified', 'converted', 'advocate'].includes(l.status)).length },
                  { stage: 'Qualified', count: leads.filter(l => ['qualified', 'converted', 'advocate'].includes(l.status)).length },
                  { stage: 'Converted', count: leads.filter(l => l.status === 'converted').length },
                  { stage: 'Advocate', count: leads.filter(l => l.status === 'advocate').length },
                ]} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="stage" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="count" fill="#F5831F" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Subreddits */}
          {subredditChartData.length > 0 && (
            <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Top Subreddits by Lead Volume</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={subredditChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip /><Bar dataKey="count" fill="#7B4FD8" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ═══ Lead Detail Panel (Slide-out) ═══ */}
      {selectedLead && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail-overlay">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedLead(null)} />
          <div className="relative w-full max-w-md bg-white shadow-xl flex flex-col animate-in slide-in-from-right" data-testid="lead-detail-panel">
            {/* Header */}
            <div className="p-4 border-b flex items-start justify-between" style={{ borderColor: '#E0E0E0' }}>
              <div className="flex-1 pr-3">
                <p className="text-xs font-medium mb-1" style={{ color: '#7B4FD8' }}>r/{selectedLead.subreddit}</p>
                <h3 className="text-sm font-bold leading-snug" style={{ color: '#1A1209' }} data-testid="detail-title">{selectedLead.title}</h3>
              </div>
              <button onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-gray-600 mt-0.5" data-testid="close-detail-btn">
                <X size={18} />
              </button>
            </div>

            {/* Detail body — scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Meta row */}
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                  style={{ backgroundColor: `${EMOTION_COLORS[selectedLead.emotion_category] || '#666'}15`, color: EMOTION_COLORS[selectedLead.emotion_category] || '#666' }}>
                  {selectedLead.emotion_category}
                </span>
                <span className="px-2 py-0.5 text-xs rounded-full font-medium"
                  style={{ backgroundColor: (STATUS_COLORS[selectedLead.status] || STATUS_COLORS.new).bg, color: (STATUS_COLORS[selectedLead.status] || STATUS_COLORS.new).text }}>
                  {selectedLead.status}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${selectedLead.score >= 70 ? 'bg-green-50 text-green-700' : selectedLead.score >= 30 ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>
                  Score: {selectedLead.score}
                </span>
              </div>

              {/* Post preview */}
              {selectedLead.text && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs leading-relaxed" style={{ color: '#444' }}>{selectedLead.text}</p>
                </div>
              )}

              {/* Info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <p style={{ color: '#374151' }}>Author</p>
                  <p className="font-medium" style={{ color: '#333' }}>u/{selectedLead.author || '—'}</p>
                </div>
                <div>
                  <p style={{ color: '#374151' }}>Discovered</p>
                  <p className="font-medium" style={{ color: '#333' }}>{selectedLead.discovered_at?.slice(0, 10)}</p>
                </div>
                <div>
                  <p style={{ color: '#374151' }}>Search Query</p>
                  <p className="font-medium" style={{ color: '#333' }}>&quot;{selectedLead.search_query}&quot;</p>
                </div>
                <div>
                  <p style={{ color: '#374151' }}>Source</p>
                  <p className="font-medium" style={{ color: '#333' }}>{selectedLead.source}</p>
                </div>
              </div>

              {/* Status selector */}
              <div>
                <p className="text-xs font-medium mb-2" style={{ color: '#374151' }}>Status</p>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map(s => (
                    <button key={s} onClick={() => { updateLeadStatus(selectedLead.lead_id, s); setSelectedLead(prev => ({ ...prev, status: s })); }}
                      className={`px-3 py-1.5 text-xs rounded-full font-medium capitalize transition-all ${selectedLead.status === s ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                      style={{ backgroundColor: STATUS_COLORS[s].bg, color: STATUS_COLORS[s].text, ...(selectedLead.status === s ? { ringColor: STATUS_COLORS[s].text } : {}) }}
                      data-testid={`detail-status-${s}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* View original post */}
              <a href={selectedLead.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors hover:bg-gray-50"
                style={{ borderColor: '#E0E0E0', color: '#F5831F' }} data-testid="detail-view-post">
                <ExternalLink size={13} /> View Original Reddit Post
              </a>

              {/* Generate AI Response button */}
              <button
                onClick={() => generateResponseForLead(selectedLead.lead_id)}
                disabled={generatingFor === selectedLead.lead_id}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg text-white disabled:opacity-50 transition-colors"
                style={{ backgroundColor: '#7B4FD8' }}
                data-testid="generate-response-btn">
                {generatingFor === selectedLead.lead_id
                  ? <><Loader2 size={13} className="animate-spin" /> Generating...</>
                  : <><MessageSquare size={13} /> Generate AI Response</>
                }
              </button>

              {/* ─── Notes Section ─── */}
              <div className="border-t pt-4" style={{ borderColor: '#E0E0E0' }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>Outreach Notes</p>

                {/* Note input */}
                <div className="flex gap-2 mb-4">
                  <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                    placeholder="Add a note... (e.g. 'Replied to their post, they asked about pricing')"
                    className="flex-1 px-3 py-2 border rounded-lg text-sm resize-none"
                    style={{ borderColor: '#E0E0E0', minHeight: '60px' }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveNote(); } }}
                    data-testid="note-input" />
                  <button onClick={saveNote} disabled={savingNote || !noteText.trim()}
                    className="self-end px-3 py-2 rounded-lg text-white disabled:opacity-40 transition-colors"
                    style={{ backgroundColor: '#7B4FD8' }} data-testid="save-note-btn">
                    {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  </button>
                </div>

                {/* Notes list */}
                {leadNotes.length === 0 ? (
                  <p className="text-xs text-center py-3" style={{ color: '#374151' }} data-testid="no-notes-msg">No notes yet. Add your first outreach note above.</p>
                ) : (
                  <div className="space-y-3">
                    {leadNotes.map((note, i) => (
                      <div key={note.note_id || i} className="bg-gray-50 rounded-lg p-3" data-testid={`note-${i}`}>
                        <p className="text-sm leading-relaxed" style={{ color: '#333' }}>{note.text}</p>
                        <p className="text-xs mt-2" style={{ color: '#374151' }}>
                          {note.created_at?.slice(0, 10)} at {note.created_at?.slice(11, 16)} &middot; {note.author}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminLeads;
